// ─────────────────────────────────────────────────────────────────────────────
//  src/utils/pdfWatermark.js
//  Loads /logo_eagle.png once and stamps it as a faint centred watermark on
//  every page of a jsPDF document. Used by Employee, Client, Reports and
//  Scoring PDF exports.
// ─────────────────────────────────────────────────────────────────────────────

let _cachedDataUrl = null;
let _inflight = null;

/** Fetches /logo_eagle.png (from /public) and returns a base64 data URL.
 *  Cached after the first successful call. Returns null on failure so callers
 *  can no-op silently if the asset is missing. */
export async function loadWatermarkDataUrl() {
  if (_cachedDataUrl) return _cachedDataUrl;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await fetch("/logo_eagle.png");
      if (!res.ok) return null;
      const blob = await res.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
      });
      _cachedDataUrl = dataUrl;
      return dataUrl;
    } catch {
      return null;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

/** Stamps the eagle logo on every page of a jsPDF document. Call at the END
 *  of PDF generation (just before doc.save / doc.output). With ~8% opacity
 *  the watermark sits over the content but stays faint enough for the text
 *  to remain legible. */
export function applyPdfWatermark(doc, dataUrl) {
  if (!doc || !dataUrl) return;
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    // Watermark width = 60% of the shorter page side so it scales sensibly
    // for both portrait and landscape pages without overflowing.
    const wmSize = Math.min(pageW, pageH) * 0.65;
    const x = (pageW - wmSize) / 2;
    const y = (pageH - wmSize) / 2;
    try {
      if (doc.GState) {
        doc.setGState(new doc.GState({ opacity: 0.08 }));
      }
      doc.addImage(dataUrl, "PNG", x, y, wmSize, wmSize, undefined, "FAST");
    } catch (e) {
      // Swallow per-page errors so a single bad page doesn't break the export.
    } finally {
      if (doc.GState) {
        try { doc.setGState(new doc.GState({ opacity: 1 })); } catch (e) { }
      }
    }
  }
}

/** Convenience wrapper: load + apply. Use when you don't already have the data URL. */
export async function stampWatermark(doc) {
  const dataUrl = await loadWatermarkDataUrl();
  applyPdfWatermark(doc, dataUrl);
}
